
import { useCallback, useEffect } from 'react';
import MediaUrlsHandler from '../components/MediaUrlsHandler';

interface UseProductMediaHandlerProps {
  mediaUrls: string[];
  setMediaUrls: (urls: string[]) => void;
  name: string;
  productId: string;
  shopId?: string;
}

export const useProductMediaHandler = ({
  mediaUrls,
  setMediaUrls,
  name,
  productId,
  shopId
}: UseProductMediaHandlerProps) => {
  // Initialize MediaUrlsHandler
  const mediaHandler = MediaUrlsHandler({ 
    mediaUrls, 
    setMediaUrls, 
    name, 
    productId,
    shopId
  });
  
  // Clean empty URLs on load
  useEffect(() => {
    if (mediaUrls) {
      console.log("[useProductMediaHandler] Cleaning empty URLs on load", { mediaUrls });
      mediaHandler.cleanEmptyUrls();
    }
  }, []);
  
  // Function to save product with media
  const handleSaveWithMedia = useCallback(async (handleSave: (updatedMediaUrls?: string[]) => Promise<void>) => {
    try {
      console.log("[handleSaveWithMedia] Starting save process with mediaUrls:", mediaUrls);
      console.log("[handleSaveWithMedia] previewMediaUrls:", mediaHandler.previewMediaUrls);
      
      // Clean empty URLs before saving
      mediaHandler.cleanEmptyUrls();
      
      // Commit preview URLs to get the complete list of URLs
      const updatedMediaUrls = mediaHandler.commitPreviewUrls();
      
      console.log("[handleSaveWithMedia] Combined URLs after commit:", updatedMediaUrls);
      
      // Filter out any invalid URLs
      const validUrls = updatedMediaUrls.filter(url => url && typeof url === 'string' && url.trim() !== '');
      console.log("[handleSaveWithMedia] Valid URLs after filtering:", validUrls);
      
      // Update the mediaUrls state with the valid URLs
      setMediaUrls(validUrls);
      
      // Important: We need to pass the valid URLs to handleSave directly
      // rather than relying on the state to be updated in time
      await handleSave(validUrls);
      
      console.log("[handleSaveWithMedia] Product saved successfully with media URLs:", validUrls);
    } catch (error) {
      console.error("[handleSaveWithMedia] Error saving product with media:", error);
      throw error;
    }
  }, [mediaHandler, mediaUrls, setMediaUrls]);

  return {
    mediaHandler,
    handleSaveWithMedia
  };
};
